import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { useLanguage } from "./LanguageContext";
import * as cartApi from "../api/cartApi";
import * as couponApi from "../api/couponApi";
import * as productsApi from "../api/productsApi";
import {
  loadGuestCart,
  saveGuestCart,
  clearGuestCartStorage,
  normalizeGuestCartItem,
} from "../utils/guestCart";
import {
  SHIPPING_PACKAGE_LIMITS,
  calculateCartVolume,
  willAddingExceedLimit,
  willQuantityChangeExceedLimit,
  canIncreaseItemQuantity,
} from "../config/shippingLimits";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isAuthenticated, userId } = useAuth();
  const { addToast } = useToast();
  const { t } = useLanguage();

  // Pre-warm category mapping cache in background
  useEffect(() => {
    productsApi.fetchProductCategoriesMap().catch(() => {});
  }, []);

  // Guest cart state loaded from localStorage
  const [guestCart, setGuestCart] = useState(() => loadGuestCart());

  // Authenticated server cart state
  const [serverCart, setServerCart] = useState(null);
  const [loadingServerCart, setLoadingServerCart] = useState(false);
  const [serverCartTick, setServerCartTick] = useState(0);

  // Synchronize guest cart state to localStorage whenever modified
  useEffect(() => {
    saveGuestCart(guestCart);
  }, [guestCart]);

  // Applied coupon state loaded from sessionStorage
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const stored = sessionStorage.getItem("ecommerce_applied_coupon");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Synchronize applied coupon to sessionStorage
  useEffect(() => {
    try {
      if (appliedCoupon) {
        sessionStorage.setItem("ecommerce_applied_coupon", JSON.stringify(appliedCoupon));
      } else {
        sessionStorage.removeItem("ecommerce_applied_coupon");
      }
    } catch {
      // Storage unavailable or disabled
    }
  }, [appliedCoupon]);

  // Fetch server cart only when user is authenticated
  const refreshServerCart = useCallback(() => {
    setServerCartTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setServerCart(null);
      return;
    }

    let cancelled = false;
    setLoadingServerCart(true);

    cartApi
      .getCart(userId)
      .then((c) => {
        if (!cancelled) setServerCart(c);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Could not fetch server cart:", err);
          setServerCart(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingServerCart(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, serverCartTick]);

  // Subtotal, shipping, discount, and total calculations
  const guestSubtotal = useMemo(() => {
    return guestCart.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
      0
    );
  }, [guestCart]);

  const guestCount = useMemo(() => {
    return guestCart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [guestCart]);

  const serverCount = useMemo(() => {
    return (serverCart?.items || []).reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
  }, [serverCart]);

  const serverSubtotal = useMemo(() => {
    return Number(serverCart?.totalPrice) || 0;
  }, [serverCart]);

  const cartCount = isAuthenticated ? serverCount : guestCount;
  const subtotal = isAuthenticated ? serverSubtotal : guestSubtotal;
  const shipping = 0;

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    return Number(appliedCoupon.discountAmount) || 0;
  }, [appliedCoupon]);

  const total = useMemo(() => {
    return Math.max(0, subtotal - discountAmount);
  }, [subtotal, discountAmount]);

  // Unified items array for both guest and authenticated users
  const activeCartItems = useMemo(() => {
    return isAuthenticated ? serverCart?.items || [] : guestCart;
  }, [isAuthenticated, serverCart, guestCart]);

  // Real-time package volume metrics based on Bosta shipping limits
  const cartVolume = useMemo(() => {
    return calculateCartVolume(activeCartItems);
  }, [activeCartItems]);

  const maxPackageVolume = SHIPPING_PACKAGE_LIMITS.MAX_PACKAGE_VOLUME_CM3;

  const isOverPackageLimit = useMemo(() => {
    return cartVolume > maxPackageVolume;
  }, [cartVolume, maxPackageVolume]);

  const remainingPackageVolume = useMemo(() => {
    return Math.max(0, maxPackageVolume - cartVolume);
  }, [cartVolume, maxPackageVolume]);

  const canIncreaseItem = useCallback(
    (itemId) => {
      return canIncreaseItemQuantity(activeCartItems, itemId);
    },
    [activeCartItems]
  );

  // Automatically recalculate coupon discount if subtotal changes
  useEffect(() => {
    if (!appliedCoupon) return;

    if (subtotal <= 0) {
      setAppliedCoupon(null);
      return;
    }

    let isCurrent = true;
    couponApi
      .validateCoupon({
        code: appliedCoupon.code,
        cartTotal: subtotal,
        userId: userId || undefined,
      })
      .then((res) => {
        if (!isCurrent) return;
        if (res.isValid) {
          setAppliedCoupon({
            code: res.code,
            discountAmount: res.discountAmount,
            finalTotal: res.finalTotal,
            couponType: res.couponType,
            amount: res.amount,
            message: res.message,
          });
        } else {
          // If cart no longer meets coupon criteria (e.g., minimum spend), drop coupon and notify
          setAppliedCoupon(null);
          addToast(res.message || "Coupon no longer meets requirements.", "warning");
        }
      })
      .catch(() => {
        // Silently preserve existing discount on transient network errors
      });

    return () => {
      isCurrent = false;
    };
  }, [subtotal, appliedCoupon?.code, userId, addToast]);

  const applyCoupon = useCallback(
    async (code, customSubtotal) => {
      const codeToApply = code?.trim();
      if (!codeToApply) {
        return { success: false, message: "Please enter a promo code." };
      }

      const activeSubtotal = customSubtotal != null ? customSubtotal : subtotal;
      setValidatingCoupon(true);
      try {
        const res = await couponApi.validateCoupon({
          code: codeToApply,
          cartTotal: activeSubtotal,
          userId: userId || undefined,
        });

        if (res.isValid) {
          const couponData = {
            code: res.code,
            discountAmount: res.discountAmount,
            finalTotal: res.finalTotal,
            couponType: res.couponType,
            amount: res.amount,
            message: res.message,
          };
          setAppliedCoupon(couponData);
          return { success: true, message: res.message, discountAmount: res.discountAmount };
        } else {
          return { success: false, message: res.message || "Invalid coupon code." };
        }
      } catch (err) {
        return { success: false, message: err.message || "Failed to validate coupon." };
      } finally {
        setValidatingCoupon(false);
      }
    },
    [subtotal, userId]
  );

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
  }, []);

  // Unified Add to Cart
  const addToCart = useCallback(
    async (itemPayload) => {
      const activeItems = isAuthenticated ? serverCart?.items || [] : guestCart;
      const qtyToAdd = Math.max(1, Number(itemPayload.quantity) || 1);
      const limitCheck = willAddingExceedLimit(activeItems, itemPayload, qtyToAdd);

      if (limitCheck.willExceed) {
        const errorMsg = t("cart.packageLimitItemCannotAdd");
        addToast(errorMsg, "warning");
        return {
          success: false,
          exceededLimit: true,
          message: errorMsg,
        };
      }

      if (!isAuthenticated) {
        // Guest user: offline in-memory + localStorage persistence
        const normalized = normalizeGuestCartItem(itemPayload);
        setGuestCart((prev) => {
          const existingIndex = prev.findIndex((i) => {
            if (normalized.sizeVariantId && normalized.colorVariantId) {
              return (
                i.productId === normalized.productId &&
                i.sizeVariantId === normalized.sizeVariantId &&
                i.colorVariantId === normalized.colorVariantId
              );
            }
            return i.id === normalized.id || i.productId === normalized.productId;
          });

          if (existingIndex > -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              quantity: updated[existingIndex].quantity + normalized.quantity,
            };
            return updated;
          }
          return [...prev, normalized];
        });
        return { success: true, isGuest: true };
      }

      // Authenticated user: backend cart API
      if (!itemPayload.sizeVariantId || !itemPayload.colorVariantId) {
        throw new Error("Size and color are required.");
      }

      const res = await cartApi.addCartItem(userId, {
        sizeVariantId: itemPayload.sizeVariantId,
        colorVariantId: itemPayload.colorVariantId,
      });
      refreshServerCart();
      return res;
    },
    [isAuthenticated, serverCart, guestCart, userId, refreshServerCart, addToast, t]
  );

  // Update quantity (guest or server)
  const updateQuantity = useCallback(
    async (itemId, quantity) => {
      const nextQty = Math.max(1, Number(quantity) || 1);
      const activeItems = isAuthenticated ? serverCart?.items || [] : guestCart;
      const targetItem = activeItems.find((i) => i.id === itemId);
      const currentQty = targetItem
        ? Math.max(1, Number(targetItem.quantity ?? targetItem.qty ?? 1))
        : 1;

      // Only check limit when increasing quantity
      if (nextQty > currentQty) {
        const limitCheck = willQuantityChangeExceedLimit(activeItems, itemId, nextQty);
        if (limitCheck.willExceed) {
          const errorMsg = t("cart.packageLimitItemCannotIncrease");
          addToast(errorMsg, "warning");
          return { success: false, exceededLimit: true, message: errorMsg };
        }
      }

      if (!isAuthenticated) {
        setGuestCart((prev) =>
          prev.map((item) => (item.id === itemId ? { ...item, quantity: nextQty } : item))
        );
        return { success: true };
      }

      try {
        const c = await cartApi.updateCartItemQuantity(userId, itemId, nextQty);
        setServerCart(c);
        return { success: true };
      } catch (err) {
        addToast(err.message || "Failed to update item quantity.", "error");
        return { success: false, message: err.message };
      }
    },
    [isAuthenticated, serverCart, guestCart, userId, addToast, t]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    async (itemId) => {
      if (!isAuthenticated) {
        setGuestCart((prev) => prev.filter((item) => item.id !== itemId));
        return;
      }

      try {
        const c = await cartApi.deleteCartItem(userId, itemId);
        setServerCart(c);
      } catch (err) {
        addToast(err.message || "Failed to remove item from cart.", "error");
      }
    },
    [isAuthenticated, userId, addToast]
  );

  // Clear guest cart
  const clearGuestCart = useCallback(() => {
    setGuestCart([]);
    clearGuestCartStorage();
  }, []);

  const value = {
    // Items
    guestCart,
    setGuestCart,
    serverCart,
    cartItems: activeCartItems,
    // Metrics
    cartCount,
    subtotal,
    shipping,
    discountAmount,
    total,
    loadingServerCart,
    // Package limits & volume
    cartVolume,
    maxPackageVolume,
    isOverPackageLimit,
    remainingPackageVolume,
    canIncreaseItem,
    // Coupon
    appliedCoupon,
    validatingCoupon,
    applyCoupon,
    removeCoupon,
    // Operations
    addToCart,
    updateQuantity,
    removeFromCart,
    clearGuestCart,
    refreshServerCart,
    // Auth flag
    isGuest: !isAuthenticated,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
